"use client";

import { primaryColor } from "@/lib/theme";
import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { createPortal } from "react-dom";
import { MapPin, X, Search } from "lucide-react";
import LocalityLabel from "@/components/Atoms/LocalityLabel";

interface RouteLocation {
  id: number;
  name: string;
  state: string;
  slug: string;
  ibge_id?: number;
  /** Full dropoff point name ("CURITIBA GARAGEM"), used only for display. */
  point_name?: string;
}

interface DropoffPoint {
  id: number;
  name: string;
  address: string | null;
  number: string | null;
  complement: string | null;
  district: string | null;
  cep: string | null;
  phone: string | null;
  available_hours: string | null;
  ibge_id: number;
  city_name: string;
  state_uf: string;
}

interface Props {
  /** Scope used to build the DOM ids of this input's buttons (e.g. "origin"). */
  idScope: string;
  placeholder: string;
  value: string;
  selectedLocation: RouteLocation | null;
  onChange: (v: string) => void;
  onSelect: (loc: RouteLocation) => void;
  onClear: () => void;
  excludeId?: number | null;
  inputStyles?: string;
  operatorId?: number;
  loading?: boolean;
  allRoutes: DropoffPoint[];
  fadeValue?: boolean;
  onAfterSelect?: () => void;
}

const toTitleCase = (str: string) =>
  str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const normalize = (str: string) =>
  str.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Returns a score > 0 if query fuzzy-matches target, 0 otherwise.
// Higher score = better match. Exact prefix > substring > fuzzy.
function fuzzyScore(target: string, query: string): number {
  if (!query) return 1;
  const t = normalize(target);
  const q = normalize(query);

  if (t === q) return 1000;
  if (t.startsWith(q)) return 500 + (100 - q.length);
  if (t.includes(q)) return 200 + (100 - q.length);

  // Character-sequence fuzzy: all query chars must appear in order in target
  let ti = 0;
  let qi = 0;
  let gaps = 0;
  while (ti < t.length && qi < q.length) {
    if (t[ti] === q[qi]) {
      qi++;
    } else {
      gaps++;
    }
    ti++;
  }
  if (qi < q.length) return 0; // not all chars matched
  return Math.max(1, 100 - gaps);
}

export interface RouteSearchInputHandle {
  focus: () => void;
}

const RouteSearchInput = forwardRef<RouteSearchInputHandle, Props>(
  function RouteSearchInput(
    {
      idScope,
      placeholder,
      value,
      selectedLocation,
      onChange,
      onSelect,
      onClear,
      excludeId,
      inputStyles,
      loading = false,
      allRoutes,
      fadeValue = false,
      onAfterSelect,
    }: Props,
    ref
  ) {
    const [focused, setFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        setFocused(true);
        inputRef.current?.focus();
      },
    }));
    const listRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
      if (!focused) return;
      const update = () => {
        const el = wrapperRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setDropdownStyle({
          position: "fixed",
          top: r.bottom + 6,
          left: r.left,
          width: r.width,
          zIndex: 99999,
        });
      };
      update();
      window.addEventListener("scroll", update, true);
      window.addEventListener("resize", update);
      return () => {
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("resize", update);
      };
    }, [focused]);

    const filtered = (() => {
      if (!focused || selectedLocation) return [];
      const q = value.trim();
      const base =
        excludeId != null
          ? allRoutes.filter((r) => r.id !== excludeId)
          : allRoutes;
      if (!q) return base;

      return base
        .map((r) => {
          const nameScore = fuzzyScore(r.city_name ?? r.name ?? "", q);
          const addrScore = fuzzyScore(
            [r.address, r.city_name, r.district, r.state_uf]
              .filter(Boolean)
              .join(" "),
            q
          );
          const score = Math.max(nameScore, addrScore);
          return { r, score };
        })
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .map(({ r }) => r);
    })();

    const showDropdown =
      focused &&
      !selectedLocation &&
      (filtered.length > 0 || value.trim().length > 0);

    // Auto-focus first result whenever the list changes
    useEffect(() => {
      setActiveIndex(filtered.length > 0 ? 0 : -1);
    }, [value, focused]);

    // Scroll active item into view
    useEffect(() => {
      if (activeIndex < 0 || !listRef.current) return;
      const item = listRef.current.children[activeIndex] as
        | HTMLElement
        | undefined;
      item?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showDropdown) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = filtered[activeIndex];
        if (item) {
          onSelect({
            id: item.id,
            name: item.city_name,
            state: item.state_uf,
            slug: "",
            ibge_id: item.ibge_id,
            point_name: item.name,
          });
          onChange("");
          setFocused(false);
          onAfterSelect?.();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setFocused(false);
        inputRef.current?.blur();
      }
    };

    const borderColor = focused ? primaryColor : "#E2E5E9";

    if (loading) {
      return (
        <div
          className={`relative border-2 rounded-control bg-paper flex items-center h-14 overflow-hidden ${inputStyles ?? ""}`}
          style={{ borderColor: "#E2E5E9" }}
        >
          <div className="flex items-center gap-2 px-3 w-full min-w-0">
            <div className="w-4 h-4 rounded-full bg-line animate-pulse shrink-0" />
            <div className="flex-1 h-3 rounded-full bg-line animate-pulse" />
          </div>
        </div>
      );
    }

    return (
      <div ref={wrapperRef} className="relative w-full">
        <div
          className={`relative border-2 rounded-control bg-paper flex items-center h-14 overflow-hidden ${inputStyles ?? ""}`}
          style={{ borderColor }}
        >
          {selectedLocation ? (
            <div
              className="flex items-center gap-2 px-3 w-full min-w-0 cursor-pointer outline-none"
              tabIndex={0}
              style={{
                opacity: fadeValue ? 0 : 1,
                transition: "opacity 0.15s ease",
                pointerEvents: fadeValue ? "none" : undefined,
              }}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 150)}
              onClick={() => {
                onClear();
                setFocused(true);
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                  onClear();
                  setFocused(true);
                  setTimeout(() => inputRef.current?.focus(), 0);
                }
              }}
            >
              <MapPin
                className="w-4 h-4 text-ink shrink-0"
                fill="currentColor"
                strokeWidth={0}
              />
              <span className="flex-1 min-w-0 text-sm font-semibold text-ink truncate">
                <LocalityLabel
                  name={selectedLocation.point_name}
                  cityName={selectedLocation.name}
                />
                <span className="font-normal text-slate ml-1">
                  - {selectedLocation.state}
                </span>
              </span>
              <button
                id={`btn-route-search-${idScope}-clear`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-ground text-mist hover:text-ink transition-colors"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
              </button>
            </div>
          ) : (
            <>
              <div className="pl-3 shrink-0 text-mist">
                <Search className="w-4 h-4" />
              </div>
              <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
                onKeyDown={handleKeyDown}
                className="flex-1 h-full px-2 text-sm text-ink placeholder-mist bg-transparent outline-none"
              />
            </>
          )}
        </div>

        {showDropdown &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              ref={listRef}
              style={dropdownStyle}
              role="listbox"
              className="bg-paper rounded-control border border-line shadow-float max-h-[220px] overflow-y-auto"
            >
              {filtered.length > 0 ? (
                filtered.map((item, idx) => (
                  <div
                    key={item.id}
                    role="option"
                    aria-selected={idx === activeIndex}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect({
                        id: item.id,
                        name: item.city_name,
                        state: item.state_uf,
                        slug: "",
                        ibge_id: item.ibge_id,
                        point_name: item.name,
                      });
                      onChange("");
                      onAfterSelect?.();
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`px-4 py-3 cursor-pointer border-b border-success-soft last:border-b-0 transition-colors duration-150 ${
                      idx === activeIndex ? "bg-[#F0F4F8]" : "hover:bg-ground"
                    }`}
                  >
                    <p className="text-body font-semibold text-ink">
                      <LocalityLabel
                        name={item.name}
                        cityName={item.city_name}
                      />
                    </p>
                    {(item.address || item.city_name) && (
                      <p className="text-fine text-slate mt-0.5">
                        {[
                          item.address ? toTitleCase(item.address) : null,
                          item.number && item.number.toLowerCase() !== "s/n"
                            ? item.number
                            : null,
                          item.district ? toTitleCase(item.district) : null,
                          item.city_name,
                          item.state_uf,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-body text-slate">
                  Nenhum resultado encontrado
                </div>
              )}
            </div>,
            document.body
          )}
      </div>
    );
  }
);

export default RouteSearchInput;
